import {
  ChangeEvent,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  DialogContent as ReachDialogContent,
  DialogOverlay as ReachDialogOverlay,
} from "@reach/dialog";
import {
  Container,
  Flex,
  Input,
  Link,
  PrimaryButton,
  system,
  SystemProps,
  Text,
} from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";
import { ContainedImage } from "~components/common/Image";

import { useRouter } from "next/router";
import { SlateRenderer } from "~components";
import { BaseContext } from "~context";
import { GetReleaseBySlugQuery } from "~graphql/sdk";
import { getError, handlePromise, sdk, showToast } from "~lib";
import { flicketLogo } from "~lib/helpers/constants";

const MotionOverlay = motion.custom(ReachDialogOverlay);

const MotionContent = motion.custom(ReachDialogContent);

const DialogOverlay = styled(MotionOverlay)`
  background-color: ${(p) => p.theme.colors.N100};

  display: flex;
  justify-content: center;
  align-items: center;

  position: fixed;
  top: 0;

  width: 100%;
  height: 100%;

  z-index: 1501;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    overflow: auto;

    padding-top: ${(p) => p.theme.space[4]}px;
    padding-bottom: ${(p) => p.theme.space[4]}px;
  }
`;

const DialogContent = styled(MotionContent)<SystemProps>`
  height: 100%;

  width: 100%;
  max-width: 442px;

  background-color: ${(p) => p.theme.colors.N100};

  overflow: auto;
  display: flex;
  flex-direction: column;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    overflow: visible;
    height: auto;
    max-height: 100%;
    background-color: ${(p) => p.theme.colors.transparent};
  }

  ${system}
`;

type ModalProps = SystemProps & {
  isOpen: boolean;
  onClose?: () => void;
};

const CustomModal = ({
  isOpen,
  onClose,
  children,
  ...props
}: PropsWithChildren<ModalProps>) => {
  return (
    <AnimatePresence>
      <DialogOverlay isOpen={isOpen} onDismiss={onClose}>
        {/* @ts-expect-error fix this */}
        <DialogContent
          aria-label={"dialog content"}
          initial={{
            y: -100,
          }}
          animate={{
            y: 0,
          }}
          exit={{
            y: 100,
          }}
          {...props}
        >
          {children}
        </DialogContent>
      </DialogOverlay>
    </AnimatePresence>
  );
};

type AccessModalProps = {
  release: GetReleaseBySlugQuery["getReleaseBySlug"];
  setHasAccess: (hasAccess: boolean) => void;
  hasAccess: boolean;
};

export const AccessModal = ({
  release,
  setHasAccess,
  hasAccess,
}: AccessModalProps) => {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { organization } = useContext(BaseContext);

  useEffect(() => {
    sessionStorage.removeItem("releasePasswordId");
  }, []);

  const validatePassword = async () => {
    setIsSubmitting(true);

    if (!password) {
      showToast("A code is required");
      return setIsSubmitting(false);
    }

    const { data, error } = await handlePromise(async () =>
      sdk({ orgId: organization?.id }).validateReleasePassword({
        id: release.id,
        password,
      })
    );

    if (error) {
      showToast(getError(error, "graphQL"), "error");
      return setIsSubmitting(false);
    }

    sessionStorage.setItem(
      "releasePasswordId",
      data.validateReleasePassword.id
    );
    setHasAccess(true);

    const compTicketsRemaining =
      data.validateReleasePassword.quantityComp -
      data.validateReleasePassword.totalUsedComp;

    const paidTicketsRemaining =
      data.validateReleasePassword.quantityPaid -
      data.validateReleasePassword.totalUsedPaid;

    window.alert(
      `${
        compTicketsRemaining
          ? `You can access ${compTicketsRemaining} complimentary ${
              compTicketsRemaining > 1 ? "tickets. " : "ticket. "
            }`
          : ""
      } ${
        paidTicketsRemaining
          ? `You can purchase ${paidTicketsRemaining} ${
              paidTicketsRemaining > 1 ? "tickets. " : "ticket. "
            }`
          : ""
      }`
    );

    return setIsSubmitting(true);
  };

  useEffect(() => {
    if (router?.query?.code?.length) {
      const getAccess = async () => {
        setPassword(router?.query?.code as string);
        if (password) {
          await validatePassword();
        }
      };
      void getAccess();
    }
  }, [router?.query?.code, password]);

  return (
    <CustomModal isOpen={release?.requiresAuth && !hasAccess}>
      <Flex justifyContent="center" mb={{ _: 4, md: 2 }} mt={{ _: 8, md: 0 }}>
        <Link href="/">
          <ContainedImage
            image={organization?.branding?.logo}
            fallback={flicketLogo}
            alt={`${organization?.name} logo`}
            width={80}
            height={30}
            position="center"
          />
        </Link>
      </Flex>
      <Container innerProps={{ p: { _: 2, md: 3 } }}>
        <form
          onSubmit={() => {
            void validatePassword();
          }}
        >
          <Text
            fontWeight="heavy"
            fontSize={6}
            color="N800"
            lineHeight="normal"
            textAlign="center"
            mb={2}
          >
            Access release
          </Text>
          {!!release?.presaleLogo && (
            <Flex mb={3} justifyContent="center">
              <ContainedImage
                image={release?.presaleLogo}
                fallback={flicketLogo}
                alt={`${organization?.name} presale logo`}
                width={120}
                height={30}
                position="center"
              />
            </Flex>
          )}
          <Text
            fontWeight="heavy"
            fontSize={4}
            color="N800"
            lineHeight="normal"
            mb={2}
          >
            {release?.presaleTitle ?? release?.event?.title}
          </Text>

          {release?.presaleInformation && (
            <SlateRenderer value={release.presaleInformation} />
          )}

          <Input
            mt={4}
            mb={4}
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
          />
          <PrimaryButton
            w="100%"
            disabled={!password}
            isLoading={isSubmitting}
            onClick={() => {
              void validatePassword();
            }}
            type="submit"
          >
            Enter
          </PrimaryButton>
        </form>
      </Container>
    </CustomModal>
  );
};
